import type { Definition, Heading, Nodes, Root, RootContent } from "mdast";
import { analyzeBodySource } from "./body-comments";
import { getBodySectionAliasKind } from "./body-section-aliases";
import {
  collectMarkdownCodeRanges,
  parseMarkdownAst,
  serializeMarkdownAst,
  walkMarkdownAst
} from "./markdown-parser";
import { normalizeFlexibleListIndentation } from "./markdown-indentation";
import type {
  CompiledMarkdownDocument,
  MarkdownBlockNode,
  MarkdownDiagnostic,
  MarkdownDiagnosticCode,
  MarkdownHighlightKind,
  MarkdownHighlightToken,
  MarkdownInlineNode,
  MarkdownSection,
  MarkdownTableAlign,
  SectionKind,
  SourcePosition
} from "./types";

type SourceMapResult = { source: string; outputToAuthorOffset: number[] };

type SectionAccumulator = {
  title: string;
  kind: SectionKind;
  heading?: Heading;
  sourceNodes: RootContent[];
  nodes: MarkdownBlockNode[];
};

export const MARKDOWN_LIMITS = {
  headingDepth: 2,
  listDepth: 3,
  tableColumns: 6,
  tableRows: 20,
  codeLanguageLength: 32
} as const;

const CODE_LANGUAGE = new RegExp(`^[A-Za-z0-9_+-]{1,${MARKDOWN_LIMITS.codeLanguageLength}}$`);

const diagnosticMessages: Record<MarkdownDiagnosticCode, string> = {
  commentUnclosed: "正文注释缺少结束标记 -->",
  commentStrayClose: "正文注释缺少开始标记 <!--",
  commentNested: "正文注释不支持嵌套 <!-- -->",
  h1: "不支持 # 页面级标题",
  deepHeading: "不支持 ### 或更深标题",
  nestedHeading: "章节标题只能位于文档根级",
  setextHeading: "不支持 Setext 标题",
  headingInline: "章节标题只能包含纯文本",
  image: "不支持图片语法",
  html: "不支持 HTML 或嵌入",
  footnote: "不支持脚注",
  frontmatter: "不支持 frontmatter",
  indentedCode: "不支持缩进代码块，请使用围栏代码块",
  codeFenceUnclosed: "代码围栏缺少结束标记",
  codeLanguage: "代码语言标签只允许 1–32 个字母、数字、_、+ 或 -",
  listDepth: "列表最多支持三级嵌套",
  tableSize: "表格最多支持 6 列、20 行",
  linkProtocol: "正文链接只支持 HTTPS 和邮箱"
};

export function getSectionKind(title: string): SectionKind {
  const normalized = title.trim().toLowerCase();
  const templateKind = getBodySectionAliasKind(normalized);
  if (templateKind) return templateKind;
  if (/^(现在|当下|正在做|我在做什么|now|what i'm working on|what i am working on)$/.test(normalized)) return "now";
  if (/^(我的作品|代表作品|项目|做过什么|作品|work|projects)$/.test(normalized)) return "work";
  if (/^(我能帮什么|擅长|能帮什么|can help|skills)$/.test(normalized)) return "can_help";
  if (/^(可以找我聊|合作|找我|如何和我协作|contact|collaborate|how to work with me)$/.test(normalized)) return "contact_intent";
  if (/^(背景|经历|关于我|我是谁|about|background|who i am)$/.test(normalized)) return "background";
  return "custom";
}

export function compileMarkdownDocument(authorSource: string): CompiledMarkdownDocument {
  const firstPass = parseMarkdownAst(authorSource);
  const protectedRanges = collectMarkdownCodeRanges(firstPass);
  const commentAnalysis = analyzeBodySource(authorSource, protectedRanges);
  const commentMapped = normalizeMappedSource(removeCommentsWithSourceMap(authorSource, commentAnalysis.tokens, commentAnalysis.issues));
  const normalizedIndentation = normalizeFlexibleListIndentation(commentMapped.source);
  const mapped: SourceMapResult = {
    source: normalizedIndentation.source,
    outputToAuthorOffset: normalizedIndentation.outputToInputOffset.map((offset) => (
      commentMapped.outputToAuthorOffset[Math.min(offset, commentMapped.outputToAuthorOffset.length - 1)] ?? authorSource.length
    ))
  };
  const root = parseMarkdownAst(mapped.source);
  const definitions = collectDefinitions(root);
  const usedDefinitions = new Set<string>();
  const diagnostics: MarkdownDiagnostic[] = [];
  const highlights: MarkdownHighlightToken[] = [];
  const authorLineStarts = getLineStarts(authorSource);

  const positionForAuthorOffset = (rawOffset: number): SourcePosition => {
    const offset = Math.max(0, Math.min(authorSource.length, rawOffset));
    const lineIndex = findLineIndex(authorLineStarts, offset);
    return { line: lineIndex + 1, column: offset - authorLineStarts[lineIndex] + 1, offset };
  };
  const authorOffset = (cleanOffset: number): number => {
    const clamped = Math.max(0, Math.min(mapped.outputToAuthorOffset.length - 1, cleanOffset));
    return mapped.outputToAuthorOffset[clamped] ?? authorSource.length;
  };
  const addDiagnostic = (
    code: MarkdownDiagnosticCode,
    startOffset: number,
    endOffset: number,
    params?: Record<string, string | number>
  ) => diagnostics.push({
    code,
    start: positionForAuthorOffset(authorOffset(startOffset)),
    end: positionForAuthorOffset(authorOffset(Math.max(startOffset + 1, endOffset))),
    params
  });
  const addNodeDiagnostic = (code: MarkdownDiagnosticCode, node: Nodes, params?: Record<string, string | number>) => {
    const start = node.position?.start.offset ?? 0;
    const end = node.position?.end.offset ?? start + 1;
    addDiagnostic(code, start, end, params);
  };
  const invalidDefinitions = new Set<string>();
  for (const [identifier, definition] of definitions) {
    if (normalizeAllowedLink(definition.url, sourceSlice(mapped.source, definition))) continue;
    invalidDefinitions.add(identifier);
    addNodeDiagnostic("linkProtocol", definition, { protocol: getProtocol(definition.url) });
  }

  for (const issue of commentAnalysis.issues) {
    const code = issue.code === "unclosed" ? "commentUnclosed" : issue.code === "nested" ? "commentNested" : "commentStrayClose";
    diagnostics.push({
      code,
      start: positionForAuthorOffset(issue.index),
      end: positionForAuthorOffset(Math.min(authorSource.length, issue.index + 3))
    });
  }
  for (const token of commentAnalysis.tokens) {
    if (token.kind === "comment") highlights.push({ kind: "comment", start: token.start, end: token.end });
  }

  addLexicalDiagnostics(mapped.source, root, addDiagnostic);
  collectHighlights(root, authorOffset, highlights);

  const safeSource = (node: Nodes): MarkdownBlockNode => ({ type: "source", value: sourceSlice(mapped.source, node) });
  const safeInlineSource = (node: Nodes): MarkdownInlineNode => ({ type: "source", value: sourceSlice(mapped.source, node) });
  const overlapsMalformedComment = (node: Nodes) => {
    const start = authorOffset(node.position?.start.offset ?? 0);
    const end = authorOffset(node.position?.end.offset ?? start + 1);
    return commentAnalysis.issues.some((issue) => issue.code !== "stray_close" && issue.index >= start && issue.index < end);
  };

  const transformInlineChildren = (children: Nodes[]): MarkdownInlineNode[] => children.flatMap((child): MarkdownInlineNode[] => {
    switch (child.type) {
      case "text": return [{ type: "text", value: child.value }];
      case "break": return [{ type: "break" }];
      case "inlineCode": return [{ type: "inlineCode", value: child.value }];
      case "strong": return [{ type: "strong", children: transformInlineChildren(child.children as Nodes[]) }];
      case "emphasis": return [{ type: "emphasis", children: transformInlineChildren(child.children as Nodes[]) }];
      case "delete": return [{ type: "delete", children: transformInlineChildren(child.children as Nodes[]) }];
      case "link": {
        const source = sourceSlice(mapped.source, child);
        const href = normalizeAllowedLink(child.url, source);
        if (!href) {
          addNodeDiagnostic("linkProtocol", child, { protocol: getProtocol(child.url) });
          return [safeInlineSource(child)];
        }
        child.url = href;
        return [{
          type: "link",
          href,
          title: child.title ?? undefined,
          autolink: isAutolinkLiteral(child.url, source, href) ? true : undefined,
          children: transformInlineChildren(child.children as Nodes[])
        }];
      }
      case "linkReference": {
        const identifier = normalizeIdentifier(child.identifier);
        const definition = definitions.get(identifier);
        if (!definition) return [safeInlineSource(child)];
        if (invalidDefinitions.has(identifier)) return [safeInlineSource(child)];
        const href = normalizeAllowedLink(definition.url, sourceSlice(mapped.source, child));
        if (!href) {
          return [safeInlineSource(child)];
        }
        usedDefinitions.add(identifier);
        return [{ type: "link", href, title: definition.title ?? undefined, children: transformInlineChildren(child.children as Nodes[]) }];
      }
      case "image":
      case "imageReference":
        addNodeDiagnostic("image", child);
        return [safeInlineSource(child)];
      case "html":
        if (!overlapsMalformedComment(child)) addNodeDiagnostic("html", child);
        return [safeInlineSource(child)];
      case "footnoteReference":
        addNodeDiagnostic("footnote", child);
        return [safeInlineSource(child)];
      default:
        return [safeInlineSource(child)];
    }
  });

  const transformBlock = (node: Nodes, listDepth = 0, nested = false): MarkdownBlockNode | undefined => {
    switch (node.type) {
      case "paragraph":
        return { type: "paragraph", children: transformInlineChildren(node.children as Nodes[]) };
      case "blockquote":
        return { type: "blockquote", children: node.children.map((child) => transformBlock(child, listDepth, true)).filter(isBlockNode) };
      case "list": {
        const depth = listDepth + 1;
        if (depth > MARKDOWN_LIMITS.listDepth) {
          addNodeDiagnostic("listDepth", node, { depth });
          return safeSource(node);
        }
        return {
          type: "list",
          ordered: Boolean(node.ordered),
          start: node.ordered ? node.start ?? 1 : undefined,
          items: node.children.map((item) => ({
            checked: typeof item.checked === "boolean" ? item.checked : undefined,
            children: item.children.map((child) => transformBlock(child, depth, true)).filter(isBlockNode)
          }))
        };
      }
      case "code": {
        const opening = getFenceOpening(mapped.source, node);
        if (!opening) {
          addNodeDiagnostic("indentedCode", node);
          return safeSource(node);
        }
        if (!hasClosingFence(mapped.source, node, opening.character, opening.length)) {
          addNodeDiagnostic("codeFenceUnclosed", node);
          return safeSource(node);
        }
        if (node.meta || (node.lang && !CODE_LANGUAGE.test(node.lang))) {
          addNodeDiagnostic("codeLanguage", node, { language: `${node.lang ?? ""}${node.meta ? ` ${node.meta}` : ""}`.trim() });
          return safeSource(node);
        }
        return { type: "code", value: node.value, language: node.lang ?? undefined };
      }
      case "thematicBreak":
        return { type: "thematicBreak" };
      case "table": {
        const rows = node.children;
        const columns = rows.reduce((maximum, row) => Math.max(maximum, row.children.length), 0);
        if (columns > MARKDOWN_LIMITS.tableColumns || rows.length > MARKDOWN_LIMITS.tableRows) {
          addNodeDiagnostic("tableSize", node, { columns, rows: rows.length });
          return safeSource(node);
        }
        return {
          type: "table",
          align: Array.from({ length: columns }, (_, index) => (node.align?.[index] ?? null) as MarkdownTableAlign),
          rows: rows.map((row) => row.children.map((cell) => transformInlineChildren(cell.children as Nodes[])))
        };
      }
      case "heading":
        if (nested && node.depth === 2 && isRootSectionHeading(node, mapped.source)) addNodeDiagnostic("nestedHeading", node);
        else addHeadingDiagnostic(node, mapped.source, addNodeDiagnostic);
        return safeSource(node);
      case "html":
        if (!overlapsMalformedComment(node)) addNodeDiagnostic("html", node);
        return safeSource(node);
      case "definition":
        return invalidDefinitions.has(normalizeIdentifier(node.identifier)) ? safeSource(node) : undefined;
      case "footnoteDefinition":
        addNodeDiagnostic("footnote", node);
        return safeSource(node);
      default:
        return safeSource(node);
    }
  };

  const sections: MarkdownSection[] = [];
  const canonicalChildren: RootContent[] = [];
  let current: SectionAccumulator = { title: "介绍", kind: "default", sourceNodes: [], nodes: [] };
  const flushSection = () => {
    if (!hasVisibleNodes(current.nodes)) {
      current = { title: "介绍", kind: "default", sourceNodes: [], nodes: [] };
      return;
    }
    const markdown = serializeMarkdownAst({ type: "root", children: current.sourceNodes });
    sections.push({
      id: `${sections.length}-${slugify(current.title) || "section"}`,
      title: current.title,
      kind: current.kind,
      markdown,
      nodes: current.nodes
    });
    if (current.heading) canonicalChildren.push(current.heading);
    canonicalChildren.push(...current.sourceNodes);
    current = { title: "介绍", kind: "default", sourceNodes: [], nodes: [] };
  };

  for (const child of root.children) {
    if (child.type === "definition" && !invalidDefinitions.has(normalizeIdentifier(child.identifier))) continue;
    if (child.type === "heading" && isRootSectionHeading(child, mapped.source)) {
      flushSection();
      const title = child.children.map((node) => node.type === "text" ? node.value : "").join("").trim();
      current = { title: title || "介绍", kind: title ? getSectionKind(title) : "default", heading: child, sourceNodes: [], nodes: [] };
      continue;
    }
    const safeNode = transformBlock(child);
    if (safeNode) current.nodes.push(safeNode);
    current.sourceNodes.push(child);
  }
  flushSection();

  for (const [identifier, definition] of definitions) {
    if (usedDefinitions.has(identifier)) canonicalChildren.push(definition);
  }

  const canonicalPublicMarkdown = serializeMarkdownAst({ type: "root", children: canonicalChildren });
  const visibleText = sections.flatMap((section) => [
    section.kind === "default" ? "" : section.title,
    ...section.nodes.flatMap((node) => blockVisibleText(node))
  ]).filter(Boolean).join("\n").trim();

  return {
    canonicalPublicMarkdown,
    sections,
    visibleText,
    diagnostics: dedupeDiagnostics(diagnostics),
    highlights: normalizeHighlights(highlights)
  };
}

export function parseMarkdownSections(markdown: string): MarkdownSection[] {
  return compileMarkdownDocument(markdown).sections;
}

/** Read an already compiled section so document-level link definitions remain resolved. */
export function getSectionReadingText(section: Pick<MarkdownSection, "nodes">): string {
  return section.nodes.flatMap((node) => blockVisibleText(node)).filter(Boolean).join("\n").trim();
}

export function getRenderableBodyMarkdown(markdown: string): string {
  return compileMarkdownDocument(markdown).canonicalPublicMarkdown;
}

export function findUnsupportedMarkdown(markdown: string): string[] {
  return Array.from(new Set(compileMarkdownDocument(markdown).diagnostics.map(getMarkdownDiagnosticMessage)));
}

export function getMarkdownDiagnosticMessage(diagnostic: MarkdownDiagnostic): string {
  return diagnosticMessages[diagnostic.code];
}

function removeCommentsWithSourceMap(
  source: string,
  tokens: ReturnType<typeof analyzeBodySource>["tokens"],
  issues: ReturnType<typeof analyzeBodySource>["issues"]
): SourceMapResult {
  let output = "";
  const outputToAuthorOffset: number[] = [];
  for (const token of tokens) {
    const malformed = token.kind === "comment" && issues.some((issue) => issue.index >= token.start && issue.index < token.end);
    for (let index = token.start; index < token.end; index += 1) {
      const character = source[index];
      if (token.kind === "text" || malformed) {
        outputToAuthorOffset.push(index);
        output += character;
      }
    }
  }
  outputToAuthorOffset.push(source.length);
  return { source: output, outputToAuthorOffset };
}

function normalizeMappedSource(mapped: SourceMapResult): SourceMapResult {
  let source = "";
  const offsets: number[] = [];
  for (let index = 0; index < mapped.source.length; index += 1) {
    if (mapped.source[index] === "\r") {
      offsets.push(mapped.outputToAuthorOffset[index]);
      source += "\n";
      if (mapped.source[index + 1] === "\n") index += 1;
      continue;
    }
    offsets.push(mapped.outputToAuthorOffset[index]);
    source += mapped.source[index];
  }
  offsets.push(mapped.outputToAuthorOffset[mapped.outputToAuthorOffset.length - 1]);
  return { source, outputToAuthorOffset: offsets };
}

function collectDefinitions(root: Root): Map<string, Definition> {
  const definitions = new Map<string, Definition>();
  for (const child of root.children) {
    if (child.type === "definition") definitions.set(normalizeIdentifier(child.identifier), child);
  }
  return definitions;
}

function normalizeIdentifier(identifier: string): string {
  return identifier.trim().replace(/\s+/g, " ").toLowerCase();
}

function isRootSectionHeading(node: Heading, source: string): boolean {
  if (node.depth !== 2 || node.children.some((child) => child.type !== "text")) return false;
  return /^ {0,3}##(?:[ \t]+|$)/.test(sourceSlice(source, node));
}

function addHeadingDiagnostic(
  node: Heading,
  source: string,
  add: (code: MarkdownDiagnosticCode, node: Nodes, params?: Record<string, string | number>) => void
): void {
  const raw = sourceSlice(source, node);
  if (!/^ {0,3}#{1,6}(?:[ \t]+|$)/.test(raw)) add("setextHeading", node);
  else if (node.depth === 1) add("h1", node);
  else if (node.depth > 2) add("deepHeading", node);
  else if (node.children.some((child) => child.type !== "text")) add("headingInline", node);
}

function addLexicalDiagnostics(
  source: string,
  root: Root,
  add: (code: MarkdownDiagnosticCode, start: number, end: number, params?: Record<string, string | number>) => void
): void {
  const protectedRanges = collectMarkdownCodeRanges(root);
  const footnote = /\[\^[^\]\n]+\](?::)?/g;
  for (const match of source.matchAll(footnote)) {
    const start = match.index ?? 0;
    if (!isEscapedOffset(source, start) && !isProtectedOffset(protectedRanges, start)) {
      add("footnote", start, start + match[0].length);
    }
  }
  const firstLineEnd = source.indexOf("\n");
  const firstLine = source.slice(0, firstLineEnd === -1 ? source.length : firstLineEnd).trim();
  if (firstLineEnd !== -1 && firstLine === "---") {
    const remainder = source.slice(firstLineEnd + 1);
    const closing = remainder.search(/^---\s*$/m);
    const frontmatterBody = closing >= 0 ? remainder.slice(0, closing) : "";
    if (closing >= 0 && /(?:^|\n)\s*[A-Za-z_][\w.-]*\s*:/.test(frontmatterBody)) {
      add("frontmatter", 0, firstLineEnd + 1 + closing + 3);
    }
  }
}

function collectHighlights(root: Root, authorOffset: (offset: number) => number, output: MarkdownHighlightToken[]): void {
  const kinds: Partial<Record<Nodes["type"], MarkdownHighlightKind>> = {
    heading: "heading", list: "list", blockquote: "quote", strong: "strong", emphasis: "emphasis",
    delete: "delete", code: "code", inlineCode: "code", link: "link", linkReference: "link",
    table: "table", thematicBreak: "thematicBreak"
  };
  walkMarkdownAst(root, (node) => {
    const kind = kinds[node.type] ?? (node.type === "listItem" && typeof node.checked === "boolean" ? "task" : undefined);
    const start = node.position?.start.offset;
    const end = node.position?.end.offset;
    if (!kind || start === undefined || end === undefined) return;
    output.push({ kind, start: authorOffset(start), end: authorOffset(end) });
  });
}

function normalizeAllowedLink(url: string, source: string): string | null {
  const trimmed = url.trim();
  if (/^https:\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:[^\s@]+@[^\s@]+$/i.test(trimmed)) return trimmed;
  if (/^http:\/\/www\./i.test(trimmed) && isBareWwwAutolink(trimmed, source)) return `https://${trimmed.slice(7)}`;
  return null;
}

function isBareWwwAutolink(url: string, source: string): boolean {
  const literal = source.trim();
  return /^www\.[^\s<>]+$/i.test(literal) && url.slice(7).toLowerCase() === literal.toLowerCase();
}

function isAutolinkLiteral(originalUrl: string, source: string, normalizedHref: string): boolean {
  const literal = source.trim();
  if (!literal || /[\[\]]/.test(literal)) return false;
  const value = literal.startsWith("<") && literal.endsWith(">") ? literal.slice(1, -1) : literal;
  if (originalUrl.toLowerCase() === value.toLowerCase()) return true;
  if (originalUrl.toLowerCase() === `mailto:${value}`.toLowerCase()) return true;
  return normalizedHref.toLowerCase() === `https://${value}`.toLowerCase() && /^www\./i.test(value);
}

function getProtocol(url: string): string {
  return /^([A-Za-z][A-Za-z0-9+.-]*):/.exec(url.trim())?.[1].toLowerCase() ?? "unknown";
}

function getFenceOpening(source: string, node: Nodes): { character: "`" | "~"; length: number } | null {
  const start = node.position?.start.offset;
  if (start === undefined) return null;
  const match = /^(`{3,}|~{3,})/.exec(source.slice(start));
  return match ? { character: match[1][0] as "`" | "~", length: match[1].length } : null;
}

function hasClosingFence(source: string, node: Nodes, character: "`" | "~", length: number): boolean {
  const endLine = node.position?.end.line;
  if (!endLine) return false;
  const line = source.split("\n")[endLine - 1] ?? "";
  const marker = character === "`" ? "`" : "~";
  return new RegExp(`^(?:\\s*> ?)*\\s*${marker}{${length},}\\s*$`).test(line);
}

function sourceSlice(source: string, node: Nodes): string {
  const start = node.position?.start.offset ?? 0;
  const end = node.position?.end.offset ?? start;
  return source.slice(start, end);
}

function blockVisibleText(node: MarkdownBlockNode, options?: { includeSource?: boolean; includeAutolinks?: boolean }): string[] {
  const includeSource = Boolean(options?.includeSource);
  switch (node.type) {
    case "paragraph": return [inlineVisibleText(node.children, options)];
    case "blockquote": return node.children.flatMap((child) => blockVisibleText(child, options));
    case "list": return node.items.flatMap((item) => item.children.flatMap((child) => blockVisibleText(child, options)));
    case "code": return [node.value];
    case "table": return node.rows.flatMap((row) => row.map((cell) => inlineVisibleText(cell, options)));
    case "source": return includeSource ? [node.value] : [];
    case "thematicBreak": return [];
  }
}

function inlineVisibleText(nodes: MarkdownInlineNode[], options?: { includeSource?: boolean; includeAutolinks?: boolean }): string {
  return nodes.map((node) => {
    switch (node.type) {
      case "text":
      case "inlineCode": return node.value;
      case "source": return options?.includeSource ? node.value : "";
      case "break": return "\n";
      case "link": return node.autolink && !options?.includeAutolinks ? "" : inlineVisibleText(node.children, options);
      case "strong":
      case "emphasis":
      case "delete": return inlineVisibleText(node.children, options);
    }
  }).join("");
}

function hasVisibleNodes(nodes: MarkdownBlockNode[]): boolean {
  return nodes.some((node) => (
    blockVisibleText(node, { includeSource: true, includeAutolinks: true }).join("").trim()
    || node.type === "thematicBreak"
  ));
}

function isBlockNode(node: MarkdownBlockNode | undefined): node is MarkdownBlockNode {
  return Boolean(node);
}

function dedupeDiagnostics(diagnostics: MarkdownDiagnostic[]): MarkdownDiagnostic[] {
  const seen = new Set<string>();
  return diagnostics.filter((diagnostic) => {
    const key = `${diagnostic.code}:${diagnostic.start.offset}:${diagnostic.end.offset}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).sort((left, right) => left.start.offset - right.start.offset || left.end.offset - right.end.offset);
}

function normalizeHighlights(highlights: MarkdownHighlightToken[]): MarkdownHighlightToken[] {
  const seen = new Set<string>();
  return highlights.filter((highlight) => {
    if (highlight.end <= highlight.start) return false;
    const key = `${highlight.kind}:${highlight.start}:${highlight.end}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).sort((left, right) => left.start - right.start || right.end - left.end);
}

function getLineStarts(source: string): number[] {
  const starts = [0];
  for (let index = 0; index < source.length; index += 1) if (source[index] === "\n") starts.push(index + 1);
  return starts;
}

function findLineIndex(starts: number[], offset: number): number {
  let low = 0;
  let high = starts.length - 1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    if (starts[middle] <= offset) low = middle + 1;
    else high = middle - 1;
  }
  return Math.max(0, high);
}

function isProtectedOffset(ranges: { start: number; end: number }[], offset: number): boolean {
  return ranges.some((range) => range.start <= offset && offset < range.end);
}

function isEscapedOffset(source: string, offset: number): boolean {
  let backslashes = 0;
  for (let index = offset - 1; index >= 0 && source[index] === "\\"; index -= 1) backslashes += 1;
  return backslashes % 2 === 1;
}

function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9\u3400-\u9fff]+/g, "-").replace(/^-|-$/g, "");
}
